"use client";

import { useState, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNativeLocation } from "@/hooks/use-native-location";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Play, Square, Loader2, MapPin } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";

export function TrackButton() {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);

  const activeShift = useQuery(api.shifts.getActiveShift);
  const startShift = useMutation(api.shifts.startShift);
  const endShift = useMutation(api.shifts.endShift);
  const batchSyncLocationPoints = useMutation(api.shifts.batchSyncLocationPoints);

  const {
    isReady,
    startTracking,
    stopTracking,
    getCurrentPosition,
    getLocationPoints,
    clearSession,
  } = useNativeLocation();

  const isActive = activeShift && activeShift.status !== "completed";

  // Calculate duration and distance for stop dialog
  const duration = activeShift
    ? Math.round(
        (Date.now() - activeShift.startedAt - (activeShift.pauseDuration || 0)) /
          60000
      )
    : 0;
  const distance = activeShift?.totalMiles ?? 0;

  const handleStart = useCallback(async () => {
    if (!isReady) return;
    setIsLoading(true);
    setShowConfirm(false);

    try {
      const position = await getCurrentPosition();
      if (!position) throw new Error("No position");

      const shiftId = await startShift({
        lat: position.lat,
        lng: position.lng,
        type: "commute", // Always commute from header button
      });

      // Start LOCAL tracking (no server calls during tracking)
      const trackingStarted = await startTracking({
        shiftId: shiftId,
        initialLat: position.lat,
        initialLng: position.lng,
      });

      if (!trackingStarted) {
        // Tracking failed to start - likely permission issue
        // End the shift we just created since we can't track it
        await endShift({ shiftId });
        toast({
          title: "Location permission required",
          description:
            'Enable "Always" location access in Settings to track mileage in the background.',
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      console.error("Failed:", error);
      toast({
        title: "Failed to start tracking",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [isReady, getCurrentPosition, startShift, startTracking, endShift]);

  const handleStop = useCallback(async () => {
    if (!activeShift) return;
    setIsLoading(true);
    setShowStopConfirm(false);

    try {
      // Get location points from native storage BEFORE stopping tracking
      const nativePoints = await getLocationPoints(activeShift._id);

      // Stop tracking and get accumulated data from native storage
      const trackingData = await stopTracking();

      const milesToSave = trackingData?.totalMiles ?? 0;

      // Sync location points to Convex for map visualization
      if (nativePoints.length > 0) {
        await batchSyncLocationPoints({
          shiftId: activeShift._id,
          points: nativePoints,
        });
      }

      await endShift({
        shiftId: activeShift._id,
        totalMiles: milesToSave,
      });

      // Clear native session data after successful sync
      await clearSession(activeShift._id);

      // Show success toast
      const mins = Math.round(
        (Date.now() - activeShift.startedAt - (activeShift.pauseDuration || 0)) /
          60000
      );
      toast({
        title: "Commute saved",
        description: `${milesToSave.toFixed(1)} mi, ${mins} min`,
      });
    } catch (error) {
      console.error("Failed to stop:", error);
      toast({
        title: "Failed to save commute",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    activeShift,
    getLocationPoints,
    stopTracking,
    batchSyncLocationPoints,
    endShift,
    clearSession,
  ]);

  return (
    <>
      <Button
        className={cn(
          "h-9 w-10 p-0 relative text-white shadow hover:shadow-lg",
          isActive
            ? "bg-red-500 hover:bg-red-600"
            : "bg-green-500 hover:bg-green-600"
        )}
        onClick={() => {
          if (isActive) {
            setShowStopConfirm(true);
          } else {
            setShowConfirm(true);
          }
        }}
        disabled={!isReady || isLoading}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isActive ? (
          <Square className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4" />
        )}

        {/* Pulsing indicator when active */}
        {isActive && (
          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-white rounded-full animate-pulse border-2 border-red-500" />
        )}
      </Button>

      {/* Start Commute Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Commute Tracking</DialogTitle>
            <DialogDescription>
              Track your commute mileage and time.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleStart}
              disabled={!isReady || isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <MapPin className="h-4 w-4 mr-2" />
              )}
              Start
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stop Commute Dialog */}
      <Dialog open={showStopConfirm} onOpenChange={setShowStopConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stop Tracking?</DialogTitle>
            <DialogDescription>Your commute will be saved.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-1">
            <p className="text-2xl font-bold">{distance.toFixed(1)} mi</p>
            <p className="text-muted-foreground">{duration} min</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowStopConfirm(false)}
            >
              Continue
            </Button>
            <Button
              className="flex-1"
              onClick={handleStop}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Square className="h-4 w-4 mr-2" />
              )}
              Stop & Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
